import React from 'react'
import s from './CaseloadPie.module.scss'

const PIE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#d946ef']

/**
 * Interactive donut pie chart for caseload distribution across counsellors.
 * Counsellor-report-only component.
 */
export const CaseloadPie = ({ data, centerValue, centerLabel }) => {
	const [hovered, setHovered] = React.useState(null)
	const total = data.reduce((sum, d) => sum + d.value, 0)
	const radius = 40
	const circumference = 2 * Math.PI * radius

	const sliceMeta = React.useMemo(() => {
		let cumulative = 0
		return data.map(d => {
			const val = (d.value / Math.max(total, 1)) * circumference
			const startAngle = (cumulative / circumference) * 2 * Math.PI
			const endAngle = ((cumulative + val) / circumference) * 2 * Math.PI
			const midAngle = (startAngle + endAngle) / 2
			cumulative += val
			const tooltipRadius = 60
			return {
				label: d.label,
				value: d.value,
				offset: cumulative - val,
				dashArray: `${val} ${circumference - val}`,
				xPercent: Math.max(15, Math.min(85, 50 + tooltipRadius * Math.sin(midAngle))),
				yPercent: Math.max(15, Math.min(85, 50 - tooltipRadius * Math.cos(midAngle))),
			}
		})
	}, [data, total, circumference])

	const hoveredSlice = hovered ? sliceMeta.find(slice => slice.label === hovered) : null

	return (
		<div className={s['caseload-pie']}>
			<div className={s['caseload-pie__chart']}>
				<svg
					viewBox="0 0 100 100"
					className={s['caseload-pie__svg']}
					aria-label={`Caseload distribution: ${data.map(d => `${d.label} ${d.value}`).join(', ')}`}
				>
					{sliceMeta.map((slice, i) => {
						const isActive = hovered === slice.label
						const isDimmed = Boolean(hovered && !isActive)
						return (
							<circle
								key={slice.label}
								cx="50"
								cy="50"
								r={radius}
								fill="none"
								stroke={PIE_COLORS[i % PIE_COLORS.length]}
								strokeWidth={isActive ? '24' : '20'}
								strokeDasharray={slice.dashArray}
								strokeDashoffset={-slice.offset}
								className={`${s['caseload-pie__slice']} ${isDimmed ? s['caseload-pie__slice--dimmed'] : ''} ${isActive ? s['caseload-pie__slice--active'] : ''}`.trim()}
								onMouseEnter={() => setHovered(slice.label)}
								onMouseLeave={() => setHovered(null)}
							/>
						)
					})}
				</svg>
				<div className={s['caseload-pie__center']}>
					<strong className={s['caseload-pie__center-value']}>{centerValue}</strong>
					<span className={s['caseload-pie__center-label']}>{centerLabel}</span>
				</div>
				{hoveredSlice && (
					<div
						className={s['caseload-pie__tooltip']}
						style={{
							'--tooltip-x': `${hoveredSlice.xPercent}%`,
							'--tooltip-y': `${hoveredSlice.yPercent}%`,
						}}
					>
						{hovered}: {hoveredSlice.value} students
					</div>
				)}
			</div>
			<div className={s['caseload-pie__legend']}>
				{data.map((d, i) => {
					const isDimmed = Boolean(hovered && hovered !== d.label)
					return (
						<div
							key={d.label}
							className={`${s['caseload-pie__legend-item']} ${isDimmed ? s['caseload-pie__legend-item--dimmed'] : ''}`.trim()}
							onMouseEnter={() => setHovered(d.label)}
							onMouseLeave={() => setHovered(null)}
						>
							<span
								className={s['caseload-pie__swatch']}
								style={{ '--swatch-color': PIE_COLORS[i % PIE_COLORS.length] }}
							/>
							{d.label}
						</div>
					)
				})}
			</div>
		</div>
	)
}
